/**
 * API Error Handler for Subscription and Rate Limit Errors
 * Provides toast notifications with appropriate actions
 */
import collaborationApi from '../components/collaboration/CollaborationApi';

// Error type constants
export const ERROR_TYPES = {
  SYNC_REQUIRES_PAID_PLAN: 'SYNC_REQUIRES_PAID_PLAN',
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  MAX_COLLABORATORS_REACHED: 'MAX_COLLABORATORS_REACHED',
  MAX_FILES_REACHED: 'MAX_FILES_REACHED',
  MAX_PROJECTS_REACHED: 'MAX_PROJECTS_REACHED'
};

/**
 * Handle API errors and show appropriate toast notifications
 * @param {Error} error - Axios error object
 * @param {Function} toast - Toast notification function
 * @param {Function} navigate - Navigation function
 * @returns {boolean} - Whether the error was handled
 */
export const handleApiError = (error, toast, navigate) => {
  if (!error.response) {
    // Network error
    toast.error('Network error. Please check your connection and try again.');
    return true;
  }

  const { status, data } = error.response;

  // A file's own 403 for "you're not a collaborator on this" (server/routes/
  // files.js's GET /:fileId) is a completely different situation from a
  // subscription/rate-limit 403 -- every 403 used to fall straight into
  // handleSubscriptionError below regardless, which has no case for this
  // code and (since this response has no `data.error`/`data.success`) simply
  // returned false, i.e. showed NOTHING -- a dead-end error with no path
  // forward. Offer "Request Access" instead, the same actionable-toast
  // pattern the subscription errors below already use.
  if (status === 403 && data?.code === 'FILE_ACCESS_DENIED') {
    showFileAccessDeniedToast(data, toast);
    return true;
  }

  // Handle subscription/rate limit errors (403/429)
  if (status === 403 || status === 429) {
    return handleSubscriptionError(data, toast, navigate);
  }

  // Handle other errors
  if (status === 401) {
    // Only show session expired error and navigate if not already on login page
    if (!window.location.pathname.includes('/login')) {
      toast.error('Your session has expired. Please log in again.');
      // Clear auth tokens to prevent retry loop
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('refreshToken');
      navigate('/login');
    }
    return true;
  }

  if (status === 500) {
    toast.error('Server error. Please try again later.');
    return true;
  }

  return false;
};

/**
 * "Request Access" actionable toast for a FILE_ACCESS_DENIED 403 -- shared
 * by handleApiError above (the raw-axios interceptor path) and App.js's
 * 'fileAccessDenied' DOM event listener (apiClient.js's own separate axios
 * instance, which most of the app's real API calls actually use, dispatches
 * that event instead of calling this module directly -- see apiClient.js's
 * comment on why).
 */
export const showFileAccessDeniedToast = (data, toast) => {
  toast.warning(`You don't have access to "${data.fileName || 'this file'}".`, {
    actions: [
      {
        label: 'Request Access',
        primary: true,
        onClick: async () => {
          try {
            await collaborationApi.requestAccess(data.fileId);
            toast.success('Access requested — the owner will be notified.');
          } catch (reqError) {
            toast.error(reqError.response?.data?.message || 'Failed to request access.');
          }
        }
      }
    ],
    duration: 8000
  });
};

/**
 * Handle subscription and rate limit specific errors
 */
export const handleSubscriptionError = (data, toast, navigate) => {
  const { error: errorCode, message, currentPlan } = data;

  switch (errorCode) {
    case ERROR_TYPES.SYNC_REQUIRES_PAID_PLAN:
      toast.warning(message || 'Cloud sync requires a paid plan.', {
        actions: [
          {
            label: 'View Plans',
            primary: true,
            onClick: () => navigate('/settings#subscription')
          }
        ],
        duration: 7000
      });
      return true;

    case ERROR_TYPES.INSUFFICIENT_CREDITS:
      toast.error(message || 'You have run out of credits.', {
        actions: [
          {
            label: 'Buy Credits',
            primary: true,
            onClick: () => navigate('/settings#subscription')
          },
          {
            label: 'Upgrade Plan',
            onClick: () => navigate('/settings#subscription')
          }
        ],
        duration: 8000
      });
      return true;

    case ERROR_TYPES.RATE_LIMIT_EXCEEDED:
      const { retryAfterMinutes, _limitType } = data;
      const retryMessage = retryAfterMinutes
        ? ` Try again in ${retryAfterMinutes} ${retryAfterMinutes === 1 ? 'minute' : 'minutes'}.`
        : '';

      toast.warning(message || `Rate limit exceeded.${retryMessage}`, {
        actions: getUpgradeActions(currentPlan, navigate),
        duration: 6000
      });
      return true;

    case ERROR_TYPES.MAX_COLLABORATORS_REACHED:
      toast.warning(message || 'Maximum collaborators reached for your plan.', {
        actions: [
          {
            label: 'Upgrade Plan',
            primary: true,
            onClick: () => navigate('/settings#subscription')
          }
        ],
        duration: 6000
      });
      return true;

    case ERROR_TYPES.MAX_FILES_REACHED:
      toast.warning(message || 'Maximum files reached for your plan.', {
        actions: [
          {
            label: 'Upgrade Plan',
            primary: true,
            onClick: () => navigate('/settings#subscription')
          }
        ],
        duration: 6000
      });
      return true;

    case ERROR_TYPES.MAX_PROJECTS_REACHED:
      toast.warning(message || 'Maximum projects reached for your plan.', {
        actions: [
          {
            label: 'Upgrade Plan',
            primary: true,
            onClick: () => navigate('/settings#subscription')
          }
        ],
        duration: 6000
      });
      return true;

    default:
      if (data.success === false && data.message) {
        toast.error(data.message);
        return true;
      }
      return false;
  }
};

/**
 * Get upgrade actions based on current plan
 */
const getUpgradeActions = (currentPlan, navigate) => {
  if (!currentPlan || currentPlan === 'free') {
    return [
      {
        label: 'Upgrade to Basic',
        primary: true,
        onClick: () => navigate('/settings#subscription')
      }
    ];
  }

  if (currentPlan === 'basic') {
    return [
      {
        label: 'Upgrade to Premium',
        primary: true,
        onClick: () => navigate('/settings#subscription')
      }
    ];
  }

  if (currentPlan === 'premium') {
    return [
      {
        label: 'Upgrade to Enterprise',
        primary: true,
        onClick: () => navigate('/settings#subscription')
      }
    ];
  }

  return [];
};

/**
 * Check response headers for warnings
 */
export const checkResponseWarnings = (response, toast, navigate) => {
  const headers = response.headers;

  // Low credits warning
  if (headers['x-credits-warning'] === 'low') {
    const remaining = headers['x-credits-remaining'];
    if (remaining) {
      toast.warning(`Low credits: ${remaining} remaining`, {
        actions: [
          {
            label: 'Buy More',
            primary: true,
            onClick: () => navigate('/settings#subscription')
          }
        ],
        duration: 5000
      });
    }
  }

  // Rate limit warning (approaching limit)
  const rateLimit = parseInt(headers['x-ratelimit-limit']);
  const remaining = parseInt(headers['x-ratelimit-remaining']);

  if (rateLimit && remaining && remaining < rateLimit * 0.2) {
    // Less than 20% remaining
    toast.info(`Rate limit warning: ${remaining} requests remaining this hour`, {
      duration: 4000
    });
  }
};

/**
 * Format error message from API response
 */
export const formatErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.response?.data?.details) {
    return error.response.data.details;
  }

  if (error.message) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

/**
 * Show success toast for subscription operations
 */
export const showSubscriptionSuccess = (operation, plan, toast) => {
  const messages = {
    created: `Successfully subscribed to ${plan} plan!`,
    updated: `Successfully switched to ${plan} plan!`,
    canceled: 'Subscription canceled successfully',
    creditsPurchased: 'Credits purchased successfully!'
  };

  toast.success(messages[operation] || 'Operation successful');
};

const apiErrorHandler = {
  handleApiError,
  handleSubscriptionError,
  showFileAccessDeniedToast,
  checkResponseWarnings,
  formatErrorMessage,
  showSubscriptionSuccess,
  ERROR_TYPES
};

export default apiErrorHandler;

